import { z } from "zod";
import { uuidSchema } from "./validation-common.js";
// ============================================
// ENUMS
// ============================================
export var MessageType;
(function (MessageType) {
    MessageType["TEXT"] = "TEXT";
    MessageType["IMAGE"] = "IMAGE";
    MessageType["FILE"] = "FILE";
    MessageType["LOCATION"] = "LOCATION";
    MessageType["SYSTEM"] = "SYSTEM";
})(MessageType || (MessageType = {}));
// ============================================
// INPUT SCHEMAS
// ============================================
export const CreateConversationInputSchema = z.object({
    companyId: uuidSchema,
    isGroup: z.boolean().default(false),
    name: z.string().trim().max(100).optional(), // Required if isGroup
    participantUserIds: z.array(uuidSchema).min(1).max(50),
});
export const SendMessageInputSchema = z.object({
    conversationId: uuidSchema,
    content: z.string().trim().min(1).max(10000),
    type: z.nativeEnum(MessageType).default(MessageType.TEXT),
    attachments: z.array(z.object({
        url: z.string().url(),
        filename: z.string(),
        mimeType: z.string(),
        size: z.number().positive(),
    })).optional(),
    replyToId: uuidSchema.optional(),
});
export const MarkAsReadInputSchema = z.object({
    conversationId: uuidSchema,
    lastReadMessageId: uuidSchema.optional(), // If not provided, marks all as read
});
export const GetMessagesQuerySchema = z.object({
    cursor: uuidSchema.optional(), // Message ID for cursor-based pagination
    limit: z.coerce.number().min(1).max(100).default(50),
    before: z.coerce.date().optional(), // Get messages before this timestamp
});
export const GetConversationsQuerySchema = z.object({
    companyId: uuidSchema,
    limit: z.coerce.number().min(1).max(50).default(20),
    cursor: uuidSchema.optional(),
    unreadOnly: z.coerce.boolean().optional(),
});
// ============================================
// RESPONSE SCHEMAS
// ============================================
export const MessageResponseSchema = z.object({
    id: z.uuid(),
    conversationId: z.uuid(),
    senderId: z.uuid(),
    senderName: z.string(),
    senderAvatar: z.string().optional(),
    content: z.string(),
    type: z.nativeEnum(MessageType),
    attachments: z.array(z.object({
        url: z.string(),
        filename: z.string(),
        mimeType: z.string(),
        size: z.number(),
    })).optional(),
    replyToId: z.uuid().optional(),
    replyToPreview: z.string().optional(),
    editedAt: z.iso.datetime().nullable().optional(),
    deletedAt: z.iso.datetime().nullable().optional(),
    createdAt: z.iso.datetime(),
});
export const ConversationParticipantResponseSchema = z.object({
    userId: z.uuid(),
    userName: z.string(),
    userAvatar: z.string().optional(),
    isAdmin: z.boolean(),
    unreadCount: z.number(),
    lastReadAt: z.iso.datetime().nullable().optional(),
});
export const ConversationResponseSchema = z.object({
    id: z.uuid(),
    isGroup: z.boolean(),
    name: z.string().optional(),
    companyId: z.uuid(),
    lastMessageTime: z.iso.datetime().nullable().optional(),
    lastMessagePreview: z.string().optional(),
    lastMessageSenderId: z.uuid().optional(),
    unreadCount: z.number(),
    participants: z.array(ConversationParticipantResponseSchema),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const ConversationListResponseSchema = z.object({
    conversations: z.array(ConversationResponseSchema),
    nextCursor: z.string().optional(),
    hasMore: z.boolean(),
});
export const MessageListResponseSchema = z.object({
    messages: z.array(MessageResponseSchema),
    nextCursor: z.string().optional(),
    hasMore: z.boolean(),
});
// ============================================
// TEAM MEMBERS (for starting new chats)
// ============================================
export const GetTeamMembersQuerySchema = z.object({
    companyId: uuidSchema,
    locationId: uuidSchema.optional(),
});
export const TeamMemberResponseSchema = z.object({
    id: z.uuid(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    avatarUrl: z.string().optional(),
});
export const ChatUnreadCountResponseSchema = z.object({
    count: z.number(),
});
export const MarkAsReadResponseSchema = z.object({
    success: z.literal(true),
});
